/**
 * OnboardingBusinessInfoForm Component
 * SESSION_199: Onboarding Frontend Step 1
 * S_030_C_002: Onboarding refinement - improved text and alias collection
 *
 * Form for business owners to review and update their business information
 * during the onboarding flow. Pre-populated with existing business data.
 *
 * Editable fields: phone, email, website, description, aliases
 * Read-only fields: name, address (use address correction flow)
 */

import React, { useCallback, useState } from 'react';
import {
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Paper,
  Title,
  Text,
  Divider,
  Box,
  Badge,
  ActionIcon,
  Flex
} from '@mantine/core';
import { IconBuilding, IconPhone, IconWorld, IconMapPin, IconTag, IconPlus, IconX } from '@tabler/icons-react';
import type { OnboardingBusinessInfoFormProps, BusinessInfoFormValues } from './OnboardingBusinessInfoForm.types';
import type { UpdateBusinessByOwnerRequest } from '../../../../domain/entities/Onboarding';
import { AddressCorrectionModal } from '../AddressCorrectionModal';
import { formatPhoneForDisplay, formatPhoneAsYouType } from '../../../../utils/phoneValidation';

/**
 * Validates URL format
 */
const isValidUrl = (url: string): boolean => {
  if (!url.trim()) return true; // Empty is valid (optional field)
  try {
    // Add https:// if no protocol specified for validation
    const urlToTest = url.startsWith('http') ? url : `https://${url}`;
    new URL(urlToTest);
    return true;
  } catch {
    return false;
  }
};

/**
 * S_030_C_003: Maximum number of aliases allowed
 */
const MAX_ALIASES = 5;

/**
 * OnboardingBusinessInfoForm - Form for editing business info during onboarding
 */
export const OnboardingBusinessInfoForm: React.FC<OnboardingBusinessInfoFormProps> = ({
  business,
  onSubmit,
  onSkip,
  isLoading = false,
  userPhone,
  onAliasesCollected
}): React.ReactElement => {
  // Form state - format phone for display on initial load
  // SESSION_340: Use user's registered phone as fallback when business doesn't have it
  const [formValues, setFormValues] = useState<BusinessInfoFormValues>({
    phone: formatPhoneForDisplay(business.phone) ?? formatPhoneForDisplay(userPhone) ?? '',
    website: business.website ?? '',
    description: business.description ?? ''
  });

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof BusinessInfoFormValues, string>>>({});

  // SESSION_201: Address correction modal state
  const [showAddressCorrectionModal, setShowAddressCorrectionModal] = useState(false);

  // S_030_C_002: Alias collection state
  const [aliases, setAliases] = useState<string[]>([]);
  const [newAliasInput, setNewAliasInput] = useState('');

  /**
   * Handle field change
   */
  const handleFieldChange = useCallback((field: keyof BusinessInfoFormValues, value: string): void => {
    // Format phone as user types
    const formattedValue = field === 'phone' ? formatPhoneAsYouType(value) : value;
    setFormValues(prev => ({ ...prev, [field]: formattedValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  /**
   * Validate form before submission
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof BusinessInfoFormValues, string>> = {};

    if (!isValidUrl(formValues.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formValues]);

  /**
   * Handle form submission
   * S_030_C_003: Pass aliases to parent via callback
   */
  const handleSubmit = useCallback((e: React.FormEvent): void => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Convert empty strings to null for the API request
    // Email moved to Contact Preferences form
    const request: UpdateBusinessByOwnerRequest = {
      phone: formValues.phone.trim() || null,
      website: formValues.website.trim() || null,
      description: formValues.description.trim() || null
    };

    // S_030_C_003: Pass aliases to parent before submitting
    if (aliases.length > 0 && onAliasesCollected) {
      onAliasesCollected(aliases);
    }

    onSubmit(request);
  }, [formValues, validateForm, onSubmit, aliases, onAliasesCollected]);

  /**
   * Handle skip button click
   */
  const handleSkip = useCallback((): void => {
    if (onSkip) {
      onSkip();
    }
  }, [onSkip]);

  /**
   * S_030_C_002: Add a new alias
   * S_030_C_003: Check limit before adding
   */
  const handleAddAlias = useCallback((): void => {
    if (aliases.length >= MAX_ALIASES) return;
    const trimmedAlias = newAliasInput.trim();
    if (trimmedAlias && !aliases.includes(trimmedAlias)) {
      setAliases(prev => [...prev, trimmedAlias]);
      setNewAliasInput('');
    }
  }, [newAliasInput, aliases]);

  /**
   * S_030_C_002: Remove an alias
   */
  const handleRemoveAlias = useCallback((aliasToRemove: string): void => {
    setAliases(prev => prev.filter(alias => alias !== aliasToRemove));
  }, []);

  /**
   * S_030_C_002: Handle alias input key press (Enter to add)
   */
  const handleAliasKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAlias();
    }
  }, [handleAddAlias]);

  return (
    <>
      {/* SESSION_201: Address Correction Modal */}
      <AddressCorrectionModal
        opened={showAddressCorrectionModal}
        onClose={() => setShowAddressCorrectionModal(false)}
        businessId={business.id}
        businessName={business.name}
        currentAddress={business.address ?? ''}
      />

      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          {/* Header */}
          <Box>
            <Title order={2} mb="xs">Review Your Business Information</Title>
            <Text c="dimmed" size="sm">
              Please review and update your business details. We use this information to find mentions of your business on social media.
            </Text>
          </Box>

          <Divider />

          {/* Read-only Business Info */}
          <Stack gap="md">
            <Text fw={600} size="sm" c="dark.4">Business Details</Text>

            <Box
              style={{
                backgroundColor: 'var(--mantine-color-gray-0)',
                padding: 'var(--mantine-spacing-md)',
                borderRadius: 'var(--mantine-radius-sm)',
                border: '1px solid var(--mantine-color-gray-3)'
              }}
            >
              <Stack gap="sm">
                <Group gap="md" align="flex-start">
                  <IconBuilding size={20} color="var(--mantine-color-gray-6)" />
                  <Box>
                    <Text size="xs" c="gray.6" tt="uppercase" fw={600} lh={1.4}>Business Name</Text>
                    <Text fw={500} c="gray.7">{business.name}</Text>
                  </Box>
                </Group>

                {business.address && (
                  <Group gap="md" align="flex-start">
                    <IconMapPin size={20} color="var(--mantine-color-gray-6)" />
                    <Box>
                      <Text size="xs" c="gray.6" tt="uppercase" fw={600} lh={1.4}>Address</Text>
                      <Text fw={500} c="gray.7">{business.address}</Text>
                    </Box>
                  </Group>
                )}

                {/* SESSION_201: Button to open address correction modal */}
                <Box ml={36} mt="xs">
                  <Button
                    variant="light"
                    color="violet"
                    size="compact-xs"
                    onClick={() => setShowAddressCorrectionModal(true)}
                    style={{ width: 'auto' }}
                  >
                    Request Address Correction
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Stack>

          <Divider />

          {/* Editable Fields */}
          <Stack gap="md">
            <Text fw={600} size="sm" c="dark.4">Contact Information</Text>

            <TextInput
              label="Phone Number"
              description="We'll match mentions that include your phone number"
              placeholder="(555) 123-4567"
              leftSection={<IconPhone size={16} />}
              value={formValues.phone}
              onChange={(e) => handleFieldChange('phone', e.currentTarget.value)}
              disabled={isLoading}
            />

            <TextInput
              label="Website"
              description="We'll detect when someone shares your website URL"
              placeholder="www.business.com"
              leftSection={<IconWorld size={16} />}
              value={formValues.website}
              onChange={(e) => handleFieldChange('website', e.currentTarget.value)}
              error={errors.website}
              disabled={isLoading}
            />

            <Textarea
              label="Description"
              placeholder="Tell customers about your business..."
              minRows={3}
              maxRows={6}
              value={formValues.description}
              onChange={(e) => handleFieldChange('description', e.currentTarget.value)}
              disabled={isLoading}
            />
          </Stack>

          <Divider />

          {/* S_030_C_002: Business Aliases Section */}
          <Stack gap="md">
            <Text fw={600} size="sm" c="dark.4">Business Nicknames & Aliases</Text>
            <Text size="sm" c="gray.6">
              Add common nicknames, abbreviations, or alternate names people use for your business (e.g., &quot;McD&apos;s&quot; for McDonald&apos;s)
            </Text>

            <Group gap="xs" align="flex-end">
              <TextInput
                placeholder="Add a nickname or alias..."
                leftSection={<IconTag size={16} />}
                value={newAliasInput}
                onChange={(e) => setNewAliasInput(e.currentTarget.value)}
                onKeyDown={handleAliasKeyPress}
                disabled={isLoading || aliases.length >= MAX_ALIASES}
                style={{ flex: 1 }}
              />
              <Button
                onClick={handleAddAlias}
                leftSection={<IconPlus size={16} />}
                disabled={isLoading || !newAliasInput.trim() || aliases.length >= MAX_ALIASES}
                size="sm"
              >
                Add
              </Button>
            </Group>

            {aliases.length >= MAX_ALIASES && (
              <Text size="xs" c="dimmed">Maximum {MAX_ALIASES} aliases reached</Text>
            )}

            {aliases.length > 0 && (
              <Flex gap="xs" wrap="wrap">
                {aliases.map((alias) => (
                  <Badge
                    key={alias}
                    size="lg"
                    variant="light"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        c="inherit"
                        onClick={() => handleRemoveAlias(alias)}
                        aria-label={`Remove ${alias}`}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    }
                    style={{ paddingRight: 4 }}
                  >
                    {alias}
                  </Badge>
                ))}
              </Flex>
            )}
          </Stack>

          <Divider />

          {/* Action Buttons */}
          <Group justify="space-between">
            {onSkip && (
              <Button
                variant="subtle"
                color="gray"
                onClick={handleSkip}
                disabled={isLoading}
              >
                Skip for now
              </Button>
            )}
            <Button
              type="submit"
              loading={isLoading}
              ml={onSkip ? 0 : 'auto'}
            >
              Save & Continue
            </Button>
          </Group>
        </Stack>
        </form>
      </Paper>
    </>
  );
};
